import { useCallback, useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useAuth } from "@/features/auth/context/AuthContext";
import { formatDisplayName } from "@/shared/utils/formatters";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { visaService } from "@/features/profile/services/visaService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";

interface VisaFormData {
  visa_type: string;
  visa_number: string;
  visa_issue_date: string;
  visa_expiry_date: string;
  issuing_authority: string;
  passport_no: string;
  passport_name: string;
  passport_expiry: string;
  passport_issuing_country: string;
  joining_date: string;
  assignment_start_date: string;
}

const emptyForm: VisaFormData = {
  visa_type: "",
  visa_number: "",
  visa_issue_date: "",
  visa_expiry_date: "",
  issuing_authority: "",
  passport_no: "",
  passport_name: "",
  passport_expiry: "",
  passport_issuing_country: "",
  joining_date: "",
  assignment_start_date: "",
};

const formatDateField = (val: string | null | undefined): string => {
  if (!val) return "";
  return val.slice(0, 10);
};

const daysUntil = (dateStr: string): number | null => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const VisaInfoPage = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { canWrite } = usePermissions();
  const { userId: paramUserId } = useParams<{ userId: string }>();

  const targetUserId = paramUserId || String(userData?.id || "");
  const isViewingOther = !!paramUserId && !!userData && paramUserId !== String(userData.id);
  const isOfficer = ["officer", "admin"].includes(
    userData?.userType?.toLowerCase() || "",
  );

  if (isOfficer && !isViewingOther) {
    return <Navigate to="/settings" replace />;
  }

  const [form, setForm] = useState<VisaFormData>(emptyForm);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);
  const [visaOptions, setVisaOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Load visa + legal profile data
  useEffect(() => {
    if (!targetUserId) return;
    setLoading(true);
    visaService
      .getUserLegalProfile(targetUserId)
      .then((data: any) => {
        setForm({
          visa_type: data.visa_type || "",
          visa_number: data.visa_number || "",
          visa_issue_date: formatDateField(data.visa_issue_date),
          visa_expiry_date: formatDateField(data.visa_expiry_date),
          issuing_authority: data.issuing_authority || "",
          passport_no: data.passport_no || "",
          passport_name: data.passport_name || "",
          passport_expiry: formatDateField(data.passport_expiry),
          passport_issuing_country: data.passport_issuing_country || "",
          joining_date: formatDateField(data.joining_date),
          assignment_start_date: formatDateField(data.assignment_start_date),
        });
        setUserName(formatDisplayName(data.last_name, data.first_name, data.middle_name));
      })
      .catch(() => showToast(t("visa.fetch_error"), "error"))
      .finally(() => setLoading(false));
  }, [targetUserId, t]);

  // Fetch localized visa type options
  useEffect(() => {
    visaService
      .getVisaDropdown(i18n.language)
      .then((data: { value: string; label: string }[]) => setVisaOptions(data))
      .catch(() => setVisaOptions([]));
  }, [i18n.language]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await visaService.updateVisaInfo(targetUserId, form);
      showToast(t("visa.save_success"), "success");
    } catch {
      showToast(t("visa.update_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const visaExpiryDays = daysUntil(form.visa_expiry_date);
  const passportExpiryDays = daysUntil(form.passport_expiry);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/users"
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 active:text-gray-800 transition-colors rounded-lg shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
                {t("settings.work_visa_info")}
              </h1>
              {isViewingOther && userName && (
                <p className="text-xs text-indigo-600 mt-0.5 truncate">
                  {t("settings.viewing_user")}: {userName}
                </p>
              )}
            </div>
          </div>
          {(canWrite("visa") || !isViewingOther) && (
            <Button onClick={handleSave} loading={saving} className="shrink-0">
              {t("common.save")}
            </Button>
          )}
        </div>
      </div>

      {/* Visa & Residence */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          {t("visa.visa_details")}
        </h2>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label
                htmlFor="visa_type"
                className="block text-sm font-medium text-gray-700"
              >
                {t("visa.visa_type")}
              </label>
              <select
                id="visa_type"
                name="visa_type"
                value={form.visa_type}
                onChange={handleChange}
                className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="">{t("common.select")}</option>
                {visaOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                {form.visa_type &&
                  !visaOptions.some((opt) => opt.value === form.visa_type) && (
                    <option value={form.visa_type}>{form.visa_type}</option>
                  )}
              </select>
            </div>
            <Input
              label={t("visa.residence_no")}
              name="visa_number"
              value={form.visa_number}
              onChange={handleChange}
            />
          </div>
          <div>
            <Input
              label={t("visa.expiry_date")}
              name="visa_expiry_date"
              type="date"
              value={form.visa_expiry_date}
              onChange={handleChange}
            />
            {visaExpiryDays !== null && (
              <p
                className={`text-xs mt-1 ${visaExpiryDays <= 90 ? "text-red-500 font-medium" : "text-gray-400"}`}
              >
                {t("visa.days_left")}: {visaExpiryDays}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Passport Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          {t("visa.passport_info")}
        </h2>
        <div className="space-y-3">
          <Input
            label={t("visa.passport_name")}
            name="passport_name"
            value={form.passport_name}
            onChange={handleChange}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label={t("visa.passport_no")}
              name="passport_no"
              value={form.passport_no}
              onChange={handleChange}
            />
            <Input
              label={t("visa.issuing_country")}
              name="passport_issuing_country"
              value={form.passport_issuing_country}
              onChange={handleChange}
            />
          </div>
          <div>
            <Input
              label={t("visa.passport_expiry")}
              name="passport_expiry"
              type="date"
              value={form.passport_expiry}
              onChange={handleChange}
            />
            {passportExpiryDays !== null && (
              <p
                className={`text-xs mt-1 ${passportExpiryDays <= 180 ? "text-red-500 font-medium" : "text-gray-400"}`}
              >
                {t("visa.days_left")}: {passportExpiryDays}
              </p>
            )}
          </div>
          <Input
            label={t("visa.issuing_authority")}
            name="issuing_authority"
            value={form.issuing_authority}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Employment Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          {t("visa.employment_timeline")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label={t("visa.joining_date")}
            name="joining_date"
            type="date"
            value={form.joining_date}
            onChange={handleChange}
          />
          <Input
            label={t("visa.assignment_start")}
            name="assignment_start_date"
            type="date"
            value={form.assignment_start_date}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="mb-8" />
    </div>
  );
};
